// ── SDLC Stage Constants ──

export const STAGES = [
  'source',
  'build',
  'test',
  'ci',
  'package',
  'observe',
  'security',
  'deploy',
] as const;

export type SdlcStage = (typeof STAGES)[number];

export const STAGE_LABELS: Record<SdlcStage, string> = {
  source: 'Source',
  build: 'Build',
  test: 'Test',
  ci: 'CI / CD',
  package: 'Package',
  observe: 'Observability',
  security: 'Security',
  deploy: 'Deploy',
};

export const STAGE_DESC: Record<SdlcStage, string> = {
  source: 'Languages, frameworks, and code quality tools',
  build: 'Build systems, bundlers, and compilation',
  test: 'Testing frameworks and code coverage',
  ci: 'Continuous integration and delivery pipelines',
  package: 'Dependencies and package management',
  observe: 'Logging, tracing, metrics, and error tracking',
  security: 'Security scanning and vulnerability detection',
  deploy: 'Deployment automation and infrastructure',
};

// ── Knowledge Base Types ──

export interface KBNode {
  id: string;
  name: string;
  kind: string;
  category: string;
  sdlcStage: SdlcStage;
  description: string;
  tags: string[];
  latestVersion?: string;
  license?: string;
  connections: number;
}

export interface KBEdge {
  source: string;
  target: string;
  type: string;
}

// ── Repo Scan Types ──

export interface DetectionSignal {
  method: string;
  match: string;
  conf: number;
}

export interface DetectedTool {
  id: string;
  confidence: number;
  tier: 'detected' | 'likely' | 'possible';
  version: string | null;
  signals: DetectionSignal[];
}

export interface LanguageDetection {
  id: string;
  confidence: number;
  files: number;
  percentage: number;
  primary: boolean;
}

export interface ToolRecommendation {
  type: 'replace' | 'add';
  current?: string;
  suggested: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
  effort?: string;
  stage?: SdlcStage;
}

// ── Combined API Response ──

export interface RepoStackAnalysis {
  repository: {
    id: string;
    name: string;
    lastScanned: string;
    languages: LanguageDetection[];
  };
  detectedTools: DetectedTool[];
  recommendations: ToolRecommendation[];
  graph: {
    nodes: KBNode[];
    edges: KBEdge[];
  };
  adoption: Record<string, number>;
}

// ── Color Constants ──

export const CAT_COLORS: Record<string, string> = {
  language: '#a78bfa',
  framework: '#2dd4bf',
  database: '#34d399',
  package_manager: '#fbbf24',
  ci_cd: '#f472b6',
  build: '#fb923c',
  test: '#34d399',
  linter: '#c084fc',
  formatter: '#e879f9',
  type_checker: '#22d3ee',
  observability: '#f97316',
  deploy: '#ef4444',
  containerization: '#06b6d4',
  container_orchestration: '#0ea5e9',
  infrastructure_as_code: '#ec4899',
  auth: '#8b5cf6',
  realtime: '#14b8a6',
  message_queue: '#eab308',
  monorepo: '#84cc16',
  cms: '#d946ef',
  orm: '#22d3ee',
  security: '#ef4444',
};

export const TIER_COLORS: Record<string, string> = {
  detected: '#34d399',
  likely: '#2dd4bf',
  possible: '#fbbf24',
};

export const EDGE_COLORS: Record<string, string> = {
  pairs_with: '#64748b',
  alternative_to: '#8b5cf6',
  depends_on: '#ef4444',
  language: 'rgba(167,139,250,0.35)',
  ecosystem: 'rgba(45,212,191,0.3)',
  extends: '#f59e0b',
  conflicts_with: '#dc2626',
};

export const EDGE_DASH: Record<string, string> = {
  pairs_with: '',
  alternative_to: '6,4',
  depends_on: '',
  language: '',
  ecosystem: '',
  extends: '8,4',
  conflicts_with: '4,4',
};
